import React, { useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  CROSS_CLIENT_DEFAULT_PAGE_SIZE,
  selectCrossClientCurrentTabLoaded,
  selectCrossClientPageMeta,
  selectCrossClientPageResults,
  selectCrossClientPaging,
  selectCrossClientResultsExecution,
  selectCrossClientSelectedResultIds,
  selectCrossClientSelectedTab,
  setCrossClientPage,
  setCrossClientPageSize,
} from '../../../store/crossClient/crossClientResults'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { Spinner } from '../../../components/Spinner/Spinner'
import { ConditionalStyles, TableColumn, TableStyles } from 'react-data-table-component'
import { CrossClientQueryResult, CrossClientResultTabs } from '../../../models/CrossClient'
import { CrossClientQueryResultMatchCell } from './Cells/CrossClientQueryResultMatchCell'
import { CrossClientQueryResultSelectCell } from './Cells/CrossClientQueryResultSelectCell'
import { CrossClientResultSelectAllHeader } from './Cells/CrossClientResultSelectAllHeader'
import { CrossClientSelectionBanner } from '../Results/CrossClientSelectionBanner'
import { CrossClientQueryResultClientCell } from './Cells/CrossClientQueryResultClientCell'
import { CrossClientQueryResultOwnerCell } from './Cells/CrossClientQueryResultOwnerCell'
import { CrossClientQueryResultActionsCell } from './Cells/CrossClientQueryResultActionsCell'
import { CrossClientQueryResultEvaluateCell } from './Cells/CrossClientQueryResultEvaluateCell'
import { CrossClientNextStepCell } from './Cells/CrossClientNextStepCell'
import { CrossClientResultDetailsModal } from './Details/CrossClientResultDetailsModal'
import { useCrossClientQueryResultDetails } from './Details/hooks'
import { CrossClientResultButtons } from './Details/CrossClientResultButtons'
import { CrossClientTableEmptyState } from './CrossClientTableEmptyState'
import { CrossClientQuickActionBar } from './CrossClientQuickActionBar'
import './CrossClientTable.scss'

const TABLE_STYLES: TableStyles = {
  table: {
    style: {
      border: '1px solid var(--zocks-gray-200)',
      borderRadius: '1rem',
    },
  },
  headRow: {
    style: {
      background: 'var(--zocks-gray-200)',
      border: '1px solid var(--zocks-gray-200)',
      borderRadius: '1rem 1rem 0 0',
    },
  },
  rows: {
    style: {
      animation: 'row-in 0.4s ease-out forwards',
      '&:hover': {
        background: 'var(--zocks-gray-100) !important',
      },
      '&:not(:last-of-type)': {
        borderBottomColor: 'var(--zocks-gray-200) !important',
      },
    },
  },
  noData: {
    style: {
      borderRadius: '1rem',
    },
  },
}

const SELECTION_TABLE_STYLES: TableStyles = {
  ...TABLE_STYLES,
  table: {
    style: {
      ...TABLE_STYLES.table?.style,
      borderTop: 'none',
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
  },
}

const SELECTED_ROW_BACKGROUND = 'rgba(235, 229, 252, 0.40)'

const TABLE_LOADER = (
  <div className='d-flex flex-column align-items-center justify-content-center gap-3 py-5 w-100'>
    <Spinner />
    <div className='paragraph-medium medium'>Pulling up your results…</div>
  </div>
)

export const CrossClientTable = () => {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()

  const execution = useAppSelector(selectCrossClientResultsExecution)
  const results = useAppSelector(selectCrossClientPageResults)
  const pageMeta = useAppSelector(selectCrossClientPageMeta)
  const paging = useAppSelector(selectCrossClientPaging)
  const tabLoaded = useAppSelector(selectCrossClientCurrentTabLoaded)
  const selectedTab = useAppSelector(selectCrossClientSelectedTab)
  const selectedResultIds = useAppSelector(selectCrossClientSelectedResultIds)

  const pagingInitialized = useRef(false)
  useEffect(() => {
    if (pagingInitialized.current) {
      return
    }
    pagingInitialized.current = true
    const sizeParam = Number(searchParams.get('size'))
    if (Number.isInteger(sizeParam) && sizeParam > 0 && sizeParam !== paging.size) {
      dispatch(setCrossClientPageSize(sizeParam))
    }
    const pageParam = Number(searchParams.get('page'))
    if (Number.isInteger(pageParam) && pageParam > 1 && pageParam !== paging.page) {
      dispatch(setCrossClientPage(pageParam))
    }
  }, [dispatch, searchParams, paging.page, paging.size])

  const persistPaging = (page: number, size: number) => {
    setSearchParams(
      (prev) => {
        if (page > 1) {
          prev.set('page', String(page))
        } else {
          prev.delete('page')
        }
        if (size !== CROSS_CLIENT_DEFAULT_PAGE_SIZE) {
          prev.set('size', String(size))
        } else {
          prev.delete('size')
        }
        return prev
      },
      { replace: true },
    )
  }

  const selectedResultDetails = useCrossClientQueryResultDetails()
  const { handleShowResultDetails } = selectedResultDetails

  const { decomposition } = execution ?? {}

  const conditionalRowStyles = useMemo<ConditionalStyles<CrossClientQueryResult>[]>(() => {
    const selectedSet = new Set(selectedResultIds)
    return [
      {
        when: (row) => selectedSet.has(row.id),
        style: {
          background: SELECTED_ROW_BACKGROUND,
          '&:hover': {
            background: `${SELECTED_ROW_BACKGROUND} !important`,
          },
        },
      },
    ]
  }, [selectedResultIds])

  if (!decomposition) {
    return <div>Unable to determine decomposition for this query</div>
  }

  const isMatchTab = selectedTab === CrossClientResultTabs.MATCH
  const isEvaluateTab = selectedTab === CrossClientResultTabs.POTENTIAL || selectedTab === CrossClientResultTabs.REMAINING
  const isMissingDataTab = selectedTab === CrossClientResultTabs.MISSING_DATA
  const isNoMatchTab = selectedTab === CrossClientResultTabs.NO_MATCH
  const showMatchColumn = !isMissingDataTab && selectedTab !== CrossClientResultTabs.REMAINING

  const selectColumn: TableColumn<CrossClientQueryResult> = {
    name: <CrossClientResultSelectAllHeader />,
    width: '3.25rem',
    center: true,
    ignoreRowClick: true,
    cell: (row) => <CrossClientQueryResultSelectCell result={row} />,
  }

  const clientColumn: TableColumn<CrossClientQueryResult> = {
    selector: (row) => row.clientId,
    name: 'Client',
    grow: 14,
    cell: (row) => <CrossClientQueryResultClientCell result={row} />,
  }

  const ownerColumn: TableColumn<CrossClientQueryResult> = {
    name: 'Owner',
    grow: 9,
    cell: (row) => <CrossClientQueryResultOwnerCell result={row} />,
  }

  const matchColumn: TableColumn<CrossClientQueryResult> = {
    name: 'Match',
    grow: 5.5,
    cell: (row) => <CrossClientQueryResultMatchCell result={row} onShowDetails={() => handleShowResultDetails(row)} />,
  }

  const columns: TableColumn<CrossClientQueryResult>[] = isMissingDataTab
    ? [clientColumn, ownerColumn]
    : isNoMatchTab
      ? [selectColumn, { ...clientColumn, grow: 0, width: '24rem' }, { ...ownerColumn, grow: 0, width: '16rem' }, { ...matchColumn, grow: 0, width: '8rem' }]
      : [
          selectColumn,
          clientColumn,
          ownerColumn,
          ...(showMatchColumn ? [matchColumn] : []),
          ...(isMatchTab
            ? [
                {
                  name: 'Actions',
                  grow: 5.5,
                  cell: (row: CrossClientQueryResult) => <CrossClientQueryResultActionsCell result={row} />,
                },
                {
                  ignoreRowClick: true,
                  grow: 6,
                  center: true,
                  cell: (row: CrossClientQueryResult) => <CrossClientResultButtons result={row} />,
                },
              ]
            : []),
          ...(isEvaluateTab
            ? [
                {
                  name: 'Next step',
                  grow: 11,
                  cell: (row: CrossClientQueryResult) => <CrossClientNextStepCell result={row} />,
                },
                {
                  ignoreRowClick: true,
                  grow: 6,
                  cell: (row: CrossClientQueryResult) => <CrossClientQueryResultEvaluateCell result={row} />,
                },
              ]
            : []),
        ]

  const handlePageChanged = (page: number) => {
    dispatch(setCrossClientPage(page))
    persistPaging(page, paging.size)
  }

  const handlePageSizeChanged = (currentRowsPerPage: number, page: number) => {
    dispatch(setCrossClientPageSize(currentRowsPerPage))
    dispatch(setCrossClientPage(page))
    persistPaging(page, currentRowsPerPage)
  }

  const hasSelection = selectedResultIds.length > 0
  const showSelectionBanner = isMatchTab && hasSelection
  const showQuickActionBar = isEvaluateTab

  return (
    <>
      <CrossClientResultDetailsModal resultDetails={selectedResultDetails} decomposition={decomposition} />
      <div className='cross-client-table-area'>
        {showSelectionBanner ? <CrossClientSelectionBanner /> : null}
        <ZocksDataTable
          key={`${selectedTab}-${paging.size}`}
          columns={columns}
          data={results}
          customStyles={showSelectionBanner ? SELECTION_TABLE_STYLES : TABLE_STYLES}
          conditionalRowStyles={conditionalRowStyles}
          pagination
          paginationServer
          paginationTotalRows={pageMeta.totalElements}
          paginationDefaultPage={paging.page}
          paginationPerPage={paging.size}
          progressPending={!tabLoaded}
          progressComponent={TABLE_LOADER}
          highlightOnHover
          noTableHead={showSelectionBanner}
          className='cross-client-table'
          onChangePage={handlePageChanged}
          onChangeRowsPerPage={handlePageSizeChanged}
          noDataComponent={<CrossClientTableEmptyState />}
        />
        {showQuickActionBar ? <CrossClientQuickActionBar /> : null}
      </div>
    </>
  )
}
